/**
 * @fileoverview Notification preference helpers.
 * @description Pure helpers for the profile's Notifications tab: arranging the backend's flat
 * preference rows into a channel matrix (one row per type or category, one column per channel),
 * reading a cell's current value with the user's unsaved drafts applied, and turning those drafts
 * into the PUT body.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { toTestIdSlug } from '@/shared/lib/test-id/test-id.utils'

import type {
  NotificationPreference,
  NotificationPreferenceUpdate,
} from '@/features/notifications/services/notification-preferences.service'

/** One row of the matrix: a notification type or category with its channel cells. */
export interface PreferenceMatrixRow {
  id: string
  key: string
  cells: Record<string, NotificationPreference>
}

export interface PreferenceMatrix {
  channels: string[]
  categories: PreferenceMatrixRow[]
  types: PreferenceMatrixRow[]
}

/** Unsaved toggles, keyed by preferenceTargetId. */
export type PreferenceDrafts = Record<string, boolean>

const CHANNEL_ORDER = ['EMAIL', 'PUSH', 'IN_APP']

/**
 * @description The notification type or category a preference row belongs to.
 * @param {NotificationPreference} preference - The row.
 * @returns {string} Its type or category code ('' when the backend sent neither).
 */
export function preferenceKey(preference: NotificationPreference): string {
  return (
    (preference.preferenceScope === 'TYPE' ? preference.notificationType : preference.category) ??
    ''
  )
}

/**
 * @description A stable id for one row + channel, used for drafts and test ids.
 * @param {NotificationPreference} preference - The row.
 * @returns {string} e.g. `type:PROSPECT_ASSIGNED:EMAIL`.
 */
export function preferenceTargetId(preference: NotificationPreference): string {
  return `${preference.preferenceScope.toLowerCase()}:${preferenceKey(preference)}:${preference.channel}`
}

/**
 * @description What the server currently applies for a row (before any draft).
 * @param {NotificationPreference} preference - The row.
 * @returns {boolean} True when the notification is on.
 */
export function savedValue(preference: NotificationPreference): boolean {
  return preference.effectiveEnabled ?? preference.userPreference ?? false
}

/**
 * @description Whether the user can't change a row (fixed by the organisation or mandatory).
 * @param {NotificationPreference} preference - The row.
 * @returns {boolean} True when its toggle must be disabled.
 */
export function isPreferenceLocked(preference: NotificationPreference): boolean {
  return !preference.isConfigurable || preference.isMandatory === true
}

/**
 * @description A row's value with the user's unsaved draft applied.
 * @param {NotificationPreference} preference - The row.
 * @param {PreferenceDrafts} drafts - The unsaved toggles.
 * @returns {boolean} True when the notification would be on.
 */
export function currentValue(
  preference: NotificationPreference,
  drafts: PreferenceDrafts,
): boolean {
  return drafts[preferenceTargetId(preference)] ?? savedValue(preference)
}

/**
 * @description Sorts channels with the known ones first (Email, Push, In-app), the rest after.
 * @param {string[]} channels - The channels found in the data.
 * @returns {string[]} The ordered channels.
 */
function orderChannels(channels: Iterable<string>): string[] {
  const rank = (channel: string) => {
    const index = CHANNEL_ORDER.indexOf(channel)
    return index === -1 ? CHANNEL_ORDER.length : index
  }
  return [...channels].sort((a, b) => rank(a) - rank(b) || a.localeCompare(b))
}

/**
 * @description Arranges the flat preference rows into a channel matrix, keeping the backend's
 * order for rows. Rows without a type/category code are skipped.
 * @param {NotificationPreference[]} preferences - The rows from GET /notifications/preferences.
 * @returns {PreferenceMatrix} Category rows, type rows and the channel columns.
 */
export function buildPreferenceMatrix(preferences: NotificationPreference[]): PreferenceMatrix {
  const channels = new Set<string>()
  const rows = new Map<string, PreferenceMatrixRow & { scope: string }>()

  for (const preference of preferences) {
    const key = preferenceKey(preference)
    if (!key) continue
    const id = `${preference.preferenceScope.toLowerCase()}:${key}`
    const row = rows.get(id) ?? { id, key, scope: preference.preferenceScope, cells: {} }
    row.cells[preference.channel] = preference
    rows.set(id, row)
    channels.add(preference.channel)
  }

  const all = [...rows.values()]
  return {
    channels: orderChannels(channels),
    categories: all.filter((row) => row.scope === 'CATEGORY'),
    types: all.filter((row) => row.scope === 'TYPE'),
  }
}

/**
 * @description Builds the PUT body from the drafts: only rows that are changed and editable, each
 * with its current rowVersion (omitted when the user has no stored row yet).
 * @param {NotificationPreference[]} preferences - The latest rows from the server.
 * @param {PreferenceDrafts} drafts - The unsaved toggles.
 * @returns {NotificationPreferenceUpdate[]} The changes to send.
 */
export function buildPreferenceUpdates(
  preferences: NotificationPreference[],
  drafts: PreferenceDrafts,
): NotificationPreferenceUpdate[] {
  return preferences
    .filter((preference) => {
      const draft = drafts[preferenceTargetId(preference)]
      return (
        draft !== undefined && draft !== savedValue(preference) && !isPreferenceLocked(preference)
      )
    })
    .map((preference) => ({
      preferenceScope: preference.preferenceScope,
      ...(preference.preferenceScope === 'TYPE'
        ? { notificationType: preferenceKey(preference) }
        : { category: preferenceKey(preference) }),
      channel: preference.channel,
      isEnabled: drafts[preferenceTargetId(preference)] ?? null,
      ...(preference.rowVersion ? { rowVersion: preference.rowVersion } : {}),
    }))
}

/**
 * @description The QA locator of a matrix row, or of one of its channel cells.
 * @param {string} rowId - The row's id (e.g. `category:BROADCAST`).
 * @param {string} [channel] - The channel, for a cell (e.g. `EMAIL`).
 * @returns {string} e.g. `notification-pref-category-broadcast` or
 * `notification-pref-category-broadcast-email`.
 */
export function preferenceTestId(rowId: string, channel?: string): string {
  return `notification-pref-${toTestIdSlug(channel ? `${rowId}-${channel}` : rowId)}`
}

/**
 * @description Turns a code like `PROSPECT_ASSIGNED` into "Prospect assigned", the label used
 * until a translation exists for a type, category or channel the backend adds.
 * @param {string} code - The backend code.
 * @returns {string} A readable label.
 */
export function humanizeCode(code: string): string {
  const words = code.toLowerCase().split('_').filter(Boolean).join(' ')
  return words.charAt(0).toUpperCase() + words.slice(1)
}
